import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { IProduct } from 'interfaces/products';
import { getPartnersData } from 'services/products';
import { useSelector } from 'store';
import { selectActiveCompany } from 'store/companiesReducer';
import { formateNumberQuantityText } from 'utils/formatter';
import Button from 'components/Button';
import Card from 'components/Card';
import Tooltip from 'components/Tooltip';
import { ReactComponent as GiftIcon } from 'assets/imgs/gift.svg';
import { ReactComponent as LinkIcon } from 'assets/imgs/link.svg';
import yandexLogo from 'assets/imgs/yandex-logo.png';
import classes from './products.module.scss';

const Products: React.FC = () => {
	const navigate = useNavigate();
	const activeCompany = useSelector(selectActiveCompany);
	const [products, setProducts] = useState<IProduct[]>([]);
	const [recommendedProducts, setRecommendedProducts] = useState<IProduct[]>(
		[]
	);
	const [isLoading, setIsLoading] = useState(true);

	// TODO: ask to get icons from api
	const productsInfo: {
		[key: string]: {
			icon: string;
			promoText: string;
			description: string;
			action: { label: string; link: string };
		};
	} = {
		yandex: {
			icon: yandexLogo,
			promoText:
				'Дарим промокод на 5 000 руб. для Вашей первой рекламной кампании. Промокод активируется автоматически',
			description:
				'Рекламная подписка от Яндекс Бизнеса автоматически создаст и настроит объявления и запустит рекламу сразу на нескольких площадках Яндекса. Чтобы вы не тратили время на сложные настройки',
			action: {
				label: 'Запустить кампанию',
				link: 'create',
			},
		},
	};

	const getProducts = async (): Promise<void> => {
		setIsLoading(true);
		try {
			const newProducts = await getPartnersData();
			// TODO: rework when new api implemented
			const companyProducts: IProduct[] = [];
			const recommendedProducts: IProduct[] = [];
			newProducts.data.items.forEach((product) => {
				if (activeCompany?.partners?.[product.slug]) {
					return companyProducts.push(product);
				}
				return recommendedProducts.push(product);
			});
			setProducts(companyProducts);
			setRecommendedProducts(recommendedProducts);
		} catch (err) {
			console.log(err);
		}
		setIsLoading(false);
	};

	const handleProductClick =
		(productId: number, campaignsCount?: number) => () => {
			navigate(`./${productId}${campaignsCount ? '' : '/create'}`);
		};

	const handleProductLinkClick = (
		e: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>
	) => {
		e.stopPropagation();
	};

	useEffect(() => {
		if (activeCompany) {
			getProducts();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [activeCompany]);

	const renderProduct = ({
		id,
		name,
		slug,
		advert_campaigns_count,
		products,
	}: IProduct): JSX.Element => {
		const subclientData = activeCompany?.subclients?.[slug];
		const productInfo = productsInfo[slug];
		const isPromoCodeAvailable = !!(products || [])[0]
			?.isPromoCodeAvailable;
		return (
			<div
				className={classes.product}
				key={id}
				onClick={handleProductClick(id, advert_campaigns_count)}
			>
				<img
					className={classes.productLogo}
					src={productInfo.icon}
					alt={name}
				/>
				<div className={classes.productInfo}>
					<div className={classes.productName}>{name}</div>
					{subclientData && (
						<div className={classes.activeAdvertising}>
							{advert_campaigns_count
								? `${advert_campaigns_count} ${formateNumberQuantityText(
										advert_campaigns_count,
										[
											'рекламная кампания',
											'рекламные кампании',
											'рекламных кампаний',
										]
								  )}`
								: 'Нет рекламных кампаний'}
						</div>
					)}
				</div>
				<div className={classes.productActions}>
					{isPromoCodeAvailable && (
						<Tooltip
							className={classes.promocode}
							text={productInfo.promoText}
							icon={
								<GiftIcon className={classes.promocodeIcon} />
							}
							tooltipClassName={classes.promocodeTooltip}
						/>
					)}
					{productInfo.action && (
						<Button
							className={classes.productAction}
							text={productInfo.action.label}
							href={`./${id}/${productInfo.action.link}`}
							onClick={handleProductLinkClick}
						/>
					)}
					{productInfo.description && (
						<Tooltip
							className={classes.description}
							text={productInfo.description}
							tooltipClassName={classes.descriptionTooltip}
						/>
					)}
					{subclientData && (
						<a
							href="https://yandex.ru/business/priority"
							target="_blank"
							rel="noreferrer"
							onClick={handleProductLinkClick}
							className={classes.productLink}
						>
							<LinkIcon className={classes.linkIcon} />
						</a>
					)}
				</div>
			</div>
		);
	};

	return (
		<div className={classes.products}>
			<div className={classes.title}>Рекламные инструменты</div>
			{/* TODO: add skeleton loader */}
			{(isLoading || recommendedProducts.length > 0) && (
				<Card
					className={classes.productsContainer}
					title="Рекомендованные продукты"
					titleClassName={classes.productsTitle}
					isLoading={isLoading}
				>
					{recommendedProducts.map(renderProduct)}
				</Card>
			)}
			{(isLoading || products.length > 0) && (
				<Card
					className={classes.productsContainer}
					title="Мои продукты"
					titleClassName={classes.productsTitle}
					isLoading={isLoading}
				>
					{products.map(renderProduct)}
				</Card>
			)}
		</div>
	);
};

export default Products;
